import React from "react";
import styled from "styled-components";

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  entityName: string;
  entityType:
    | "Component"
    | "Component Version"
    | "Deployment Session"
    | "Fleet"
    | "Release"
    | "Vehicle"
    | "Vehicle Type";
  onConfirm: () => void;
  onClose?: () => void;
  onCancel?: () => void;
  warningMessage?: string;
}

export const DeleteConfirmationModal: React.FC<
  DeleteConfirmationModalProps
> = ({
  isOpen,
  entityName,
  entityType,
  onConfirm,
  onClose,
  onCancel,
  warningMessage,
}) => {
  if (!isOpen) return null;

  const handleCancel = onClose || onCancel || undefined;

  return (
    <ModalOverlay onClick={handleCancel}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <WarningIcon>⚠️</WarningIcon>
          <ModalTitle>Confirm Deletion</ModalTitle>
        </ModalHeader>

        <ModalBody>
          <WarningText>
            Are you sure you want to permanently delete the following{" "}
            {entityType}?
          </WarningText>
          <EntityNameDisplay>{entityName}</EntityNameDisplay>
          <DangerText>
            {warningMessage ||
              "This action cannot be undone. All associated data will be permanently removed."}
          </DangerText>
        </ModalBody>

        <ModalFooter>
          <CancelButton onClick={handleCancel}>Cancel</CancelButton>
          <DeleteButton onClick={onConfirm}>Delete Permanently</DeleteButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 8px;
  width: 500px;
  max-width: 90vw;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 20px 24px 16px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const WarningIcon = styled.div`
  font-size: 28px;
  line-height: 1;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #d32f2f;
`;

const ModalBody = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const WarningText = styled.p`
  margin: 0;
  font-size: 15px;
  color: #333;
  line-height: 1.5;
`;

const EntityNameDisplay = styled.div`
  padding: 12px 16px;
  background-color: #fff3e0;
  border-left: 4px solid #ff9800;
  border-radius: 4px;
  font-weight: 600;
  font-size: 15px;
  color: #e65100;
  word-break: break-word;
`;

const DangerText = styled.p`
  margin: 0;
  font-size: 13px;
  color: #d32f2f;
  font-weight: 500;
  line-height: 1.5;
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const CancelButton = styled.button`
  padding: 10px 24px;
  background: #f5f5f5;
  color: #333;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e0e0e0;
    border-color: #999;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const DeleteButton = styled.button`
  padding: 10px 24px;
  background: #d32f2f;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #c62828;
    box-shadow: 0 2px 8px rgba(211, 47, 47, 0.3);
  }

  &:active {
    transform: scale(0.98);
  }
`;
